import { type HexAlpha, type UUID, hexAlpha, pickFrom } from '@nfctron/nexus-core';

import { type CompanyEntity, type ConstructCompanyEntityInput, constructCompanyEntity } from '@root/lib/entities/company.entity';

/**
 * Organizer entity type
 * @export
 */
export type OrganizerEntity = {
	organizerId: UUID;
	name: string;
	slug: string;
	logoUrl?: string;
	organizerColors: OrganizerColorConfig;
	company: CompanyEntity;
};

/**
 * Organizer color config type
 * @export
 */
export type OrganizerColorConfig<C = HexAlpha> = {
	background: C;
	text: C;
};

/**
 * Constructs an organizer color config object
 * @param {Input} input
 * @returns {OrganizerColorConfig}
 */
export const constructOrganizerColorConfig = <Input extends ConstructOrganizerColorConfigInput = ConstructOrganizerColorConfigInput>(
	input: Input,
): OrganizerColorConfig => ({
	background: hexAlpha(input.background),
	text: hexAlpha(input.text),
});

/**
 * Input type for event color config constructor
 * @export
 */
export type ConstructOrganizerColorConfigInput = {
	background: string;
	text: string;
};

/**
 * Constructs an organizer color config object
 * @param {Input} input
 * @returns {OrganizerColorConfig}
 */
export const constructOrganizerEntity = <Input extends ConstructOrganizerEntityInput = ConstructOrganizerEntityInput>(
	input: Input,
): OrganizerEntity => ({
	...pickFrom(input, 'organizerId', 'name', 'logoUrl', 'slug'),
	company: constructCompanyEntity(input.company),
	organizerColors: constructOrganizerColorConfig(input.organizerColors),
});

/**
 * Input type for event color config constructor
 * @export
 */
export type ConstructOrganizerEntityInput = {
	organizerId: UUID;
	name: string;
	slug: string;
	logoUrl?: string;
	organizerColors: ConstructOrganizerColorConfigInput;
	company: ConstructCompanyEntityInput;
};
